import axios from 'axios';
import axiosCustomerService from '../lib/axiosCustomerService';
import type {
  CheckStatusRequest,
  CheckStatusResponse,
  SignUpRequest,
  SignInRequest,
  SignInResponse,
  CustomerProfile,
  UpdateProfileRequest,
  ECPVerifyResponse,
  ECPParentGroup,
  CustomerAddress,
  CustomerAddressCreateRequest,
  UpdateCustomerAddressRequest,
  AddressListParams,
  AddressListResponse,
  PrivilegeListParams,
  PrivilegeListResponse,
  RedemptionCreateRequest,
  Redemption,
  RedemptionListParams,
  RedemptionListResponse,
  CustomerSendCustomMessageRequest,
  CustomerSendImagesRequest,
  CustomerSendImagesResponse,
} from '../types/customer';

export class CustomerServiceError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'CustomerServiceError';
    this.statusCode = statusCode;
  }
}

/** Check if an error is a LINE ID token expiry (400 + "IdToken expired") */
export function isIdTokenExpiredError(err: unknown): boolean {
  if (!(err instanceof CustomerServiceError)) return false;
  if (err.statusCode !== 400) return false;
  return err.message.includes('IdToken expired');
}

async function request<T>(
  path: string,
  options: { method?: string; headers?: Record<string, string>; data?: unknown } = {},
): Promise<T> {
  try {
    const response = await axiosCustomerService.request<T>({
      url: path,
      method: options.method || 'GET',
      headers: options.headers,
      data: options.data,
    });

    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      // Handle 204 No Content
      if (err.response.status === 204) {
        return undefined as T;
      }

      let message = `Request failed: ${err.response.status}`;
      const body = err.response.data;
      if (body) {
        console.error(`[API Error] ${path}`, err.response.status, body);
        // Extract error description from nested structures:
        // Format 1: body.message is a stringified JSON with error_description
        // Format 2: body.stack.error_description (e.g. "IdToken expired.")
        let raw = body.message || body.error || '';
        if (typeof raw === 'string') {
          try {
            const inner = JSON.parse(raw);
            raw = inner.error_description || inner.error || raw;
          } catch {
            // not nested JSON, use as-is
          }
        }
        // Check body.stack for error_description (backend proxy format)
        if (body.stack?.error_description) {
          raw = body.stack.error_description;
        }
        message = raw || message;
      }
      throw new CustomerServiceError(message, err.response.status);
    }
    throw err;
  }
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number | boolean] => entry[1] !== undefined,
  );
  if (entries.length === 0) return '';
  const searchParams = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  );
  return `?${searchParams.toString()}`;
}

// --- Display Name Helper ---

export function getDisplayName(customer: CustomerProfile | null | undefined): string {
  if (!customer) return '';
  return `${customer.name} ${customer.surname}`.trim();
}

// --- Public Auth endpoints (no token) ---

export function checkStatus(lineIdToken: string): Promise<CheckStatusResponse> {
  const body: CheckStatusRequest = { id_token: lineIdToken };
  return request('/api/public/auth/check-status', {
    method: 'POST',
    data: body,
  });
}

export interface SignUpResult {
  response?: SignInResponse;
  pending: boolean;
}

export async function signUp(params: SignUpRequest): Promise<SignUpResult> {
  try {
    const response = await request<SignInResponse>('/api/public/auth/sign-up', {
      method: 'POST',
      data: params,
    });
    return { response, pending: false };
  } catch (err) {
    // 403 "inactive" means registration succeeded but user is pending approval
    if (err instanceof CustomerServiceError && err.statusCode === 403) {
      return { pending: true };
    }
    throw err;
  }
}

export function signIn(lineIdToken: string): Promise<SignInResponse> {
  const body: SignInRequest = { id_token: lineIdToken };
  return request('/api/public/auth/sign-in', {
    method: 'POST',
    data: body,
  });
}

export function refreshToken(): Promise<SignInResponse> {
  return request('/api/public/auth/refresh-token', {
    method: 'GET',
  });
}

// --- Public endpoints (no token) ---

export function verifyEcpCode(ecpCode: string): Promise<ECPVerifyResponse> {
  return request(`/api/public/customer/ecp/${encodeURIComponent(ecpCode)}`, {
    method: 'GET',
  });
}

export function apiHealthCheck(): Promise<{ status: string }> {
  return request('/api/public/healthz', {
    method: 'GET',
  });
}

// --- Private Auth endpoints (Bearer token) ---

export function signOut(accessToken: string): Promise<void> {
  return request('/api/private/customer/auth/sign-out', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

// --- Private Customer endpoints (Bearer token) ---

export function getCustomerProfile(accessToken: string): Promise<CustomerProfile> {
  return request('/api/private/customer/me', {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export function updateCustomerProfile(
  accessToken: string,
  data: UpdateProfileRequest,
): Promise<void> {
  return request('/api/private/customer/me', {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${accessToken}` },
    data,
  });
}

// --- Private Address endpoints (Bearer token) ---

export function getCustomerAddresses(
  accessToken: string,
  params?: AddressListParams,
): Promise<AddressListResponse> {
  const qs = params ? buildQueryString(params as Record<string, string | number | boolean | undefined>) : '';
  return request(`/api/private/customer/addresses${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export function createCustomerAddress(
  accessToken: string,
  data: CustomerAddressCreateRequest,
): Promise<CustomerAddress> {
  return request('/api/private/customer/addresses', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    data,
  });
}

export function updateCustomerAddress(
  accessToken: string,
  id: string,
  data: UpdateCustomerAddressRequest,
): Promise<CustomerAddress> {
  return request(`/api/private/customer/addresses/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${accessToken}` },
    data,
  });
}

export function deleteCustomerAddress(
  accessToken: string,
  id: string,
): Promise<void> {
  return request(`/api/private/customer/addresses/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

// --- Private ECP Ship-To endpoint (Bearer token) ---

export function getEcpShipTo(accessToken: string): Promise<ECPVerifyResponse> {
  return request('/api/private/customer/me/ecp/ship-to', {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export function getEcpParentGroup(accessToken: string): Promise<ECPParentGroup[]> {
  return request('/api/private/customer/me/ecp/parent-group', {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export function getEcpParentGroupShipTo(
  accessToken: string,
  ecpCode: string,
): Promise<ECPParentGroup[]> {
  return request(
    `/api/private/customer/me/ecp/parent-group/ship-to/${encodeURIComponent(ecpCode)}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
}

// --- Private Send Message endpoint (Bearer token) ---

export function sendMessage(
  accessToken: string,
  data: CustomerSendCustomMessageRequest,
): Promise<void> {
  return request('/api/private/customer/me/send-message', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    data,
  });
}

export function sendImages(
  accessToken: string,
  data: CustomerSendImagesRequest,
): Promise<CustomerSendImagesResponse> {
  return request('/api/private/customer/me/send-images', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    data,
  });
}

// --- Private Privilege endpoints (Bearer token) ---

export function getAvailablePrivileges(
  accessToken: string,
  params?: PrivilegeListParams,
): Promise<PrivilegeListResponse> {
  const qs = params ? buildQueryString(params as Record<string, string | number | boolean | undefined>) : '';
  return request(`/api/private/customer/privileges/available${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

// --- Private Redemption endpoints (Bearer token) ---

export function getRedemptions(
  accessToken: string,
  params?: RedemptionListParams,
): Promise<RedemptionListResponse> {
  const qs = params ? buildQueryString(params as Record<string, string | number | boolean | undefined>) : '';
  return request(`/api/private/customer/redemptions${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export function createRedemption(
  accessToken: string,
  data: RedemptionCreateRequest,
): Promise<Redemption> {
  return request('/api/private/customer/redemptions', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    data,
  });
}
